import {
  CoreLanguageType,
  LanguageCode,
  LanguageType,
  LocaleDetails,
  proxyLanguageRecord,
} from 'shared/src/types/languages.type';
import { notFoundTranslations } from './translations/not-found';

/**
 * Copy for the not-found surface.
 *
 * Two consumers, one vocabulary:
 *   1. The empty-state branch in app/[...slug]/page.tsx — a URL that resolved
 *      to no page (or to a page with no products). It has the dead slug, so it
 *      can offer a parsed query and ranked suggestions.
 *   2. The not-found.tsx boundaries — reached via notFound() from a route that
 *      has no slug context (app-origin gates, dead refs, admin). Static copy
 *      only: `title` + `body` + the recovery links.
 *
 * The high-traffic languages are written here; every other language is in
 * translations/<lang>/not-found.ts (see translations/STYLE.md).
 */
export type NotFoundUiContent = {
  /** Small eyebrow above the headline. Not the HTTP status — this surface is
   *  also reached on 200 responses, so it must not claim to be one. */
  eyebrow: string;
  /** Headline. */
  title: string;
  /** One sentence: what happened, and what to do about it. */
  body: string;
  /** Heading above the ranked suggestions, when we have any. */
  suggestionsTitle: string;
  /** Heading above the single best match. */
  bestMatchTitle: string;
  /** Caption under the prefilled search bar. */
  prefillHint: string;
  /** Recovery links. */
  goHome: string;
  browseAll: string;
  reportBrokenLink: string;
  /** Slim notice above the search bar on the recovery view (not-found.tsx /
   *  error.tsx). `{query}` is replaced with the query parsed from the URL,
   *  rendered emphasised. */
  noticeTitle: string;
  /** Human tab: the bar is submitting the parsed query right now. */
  noticeSearching: string;
  /** Bar prefilled but idle (crawler UA, no JS, or no auto-search). */
  noticePrefilled: string;
  /** error.tsx variant. */
  errorTitle: string;
  errorBody: string;
  retry: string;
  /** The /ai/ page when the job building it died with nothing saved: the
   *  card above the skeletons, and its button back to the search with the
   *  shopper's own words (lib/streaming/dead-job-recovery.ts). */
  streamLostTitle: string;
  streamLostBody: string;
  relaunch: string;
};

const notFoundUiContentCore: Partial<Record<CoreLanguageType, NotFoundUiContent>> & {
  en: NotFoundUiContent;
} = {
  // Every other language: translations/<lang>/not-found.ts. Hand-written records below win.
  ...notFoundTranslations,
  [LanguageCode.english]: {
    eyebrow: 'Page not found',
    title: "We couldn't find that page",
    body: 'The link may be outdated, or the page was removed. Tell us what you are after and we will find it.',
    suggestionsTitle: 'You might be looking for',
    bestMatchTitle: 'Did you mean',
    prefillHint: 'Prefilled from the address you tried',
    goHome: 'Home',
    browseAll: 'All categories',
    reportBrokenLink: 'Report a broken link',
    noticeTitle: 'That page isn’t available',
    noticeSearching: 'Searching for {query} instead…',
    noticePrefilled: 'Search for {query} below, or tell us what you’re after.',
    errorTitle: 'Something went wrong',
    errorBody: 'We couldn’t load this page. Try again, or search for what you need below.',
    retry: 'Try again',
    streamLostTitle: 'We couldn\'t finish this page',
    streamLostBody: 'The search stopped before the page was saved. Start it again and we will pick it up from your words.',
    relaunch: 'Search again',
  },
  [LanguageCode.french]: {
    eyebrow: 'Page introuvable',
    title: 'Nous n’avons pas trouvé cette page',
    body: 'Le lien est peut-être périmé, ou la page a été retirée. Dites-nous ce que vous cherchez et nous le trouverons.',
    suggestionsTitle: 'Vous cherchez peut-être',
    bestMatchTitle: 'Vouliez-vous dire',
    prefillHint: 'Pré-rempli à partir de l’adresse demandée',
    goHome: 'Accueil',
    browseAll: 'Toutes les catégories',
    reportBrokenLink: 'Signaler un lien brisé',
    noticeTitle: 'Cette page n’est pas disponible',
    noticeSearching: 'Nous cherchons plutôt {query}…',
    noticePrefilled: 'Cherchez {query} ci-dessous, ou dites-nous ce que vous voulez.',
    errorTitle: 'Une erreur est survenue',
    errorBody: 'Nous n’avons pas pu charger cette page. Réessayez, ou cherchez ce qu’il vous faut ci-dessous.',
    retry: 'Réessayer',
    streamLostTitle: 'Nous n’avons pas pu terminer cette page',
    streamLostBody: 'La recherche s’est arrêtée avant l’enregistrement de la page. Relancez-la et nous repartons de vos mots.',
    relaunch: 'Relancer la recherche',
  },
  [LanguageCode.spanish]: {
    eyebrow: 'Página no encontrada',
    title: 'No encontramos esa página',
    body: 'Puede que el enlace esté desactualizado o que la página se haya eliminado. Dinos qué buscas y lo encontraremos.',
    suggestionsTitle: 'Quizá buscabas',
    bestMatchTitle: 'Quisiste decir',
    prefillHint: 'Rellenado a partir de la dirección que intentaste',
    goHome: 'Inicio',
    browseAll: 'Todas las categorías',
    reportBrokenLink: 'Informar de un enlace roto',
    noticeTitle: 'Esa página no está disponible',
    noticeSearching: 'Buscamos {query} en su lugar…',
    noticePrefilled: 'Busca {query} abajo, o dinos qué necesitas.',
    errorTitle: 'Algo salió mal',
    errorBody: 'No pudimos cargar esta página. Inténtalo de nuevo, o busca lo que necesitas abajo.',
    retry: 'Reintentar',
    streamLostTitle: 'No pudimos terminar esta página',
    streamLostBody: 'La búsqueda se detuvo antes de guardar la página. Vuelve a lanzarla y retomaremos tus palabras.',
    relaunch: 'Buscar de nuevo',
  },
  [LanguageCode.german]: {
    eyebrow: 'Seite nicht gefunden',
    title: 'Wir konnten diese Seite nicht finden',
    body: 'Der Link ist möglicherweise veraltet oder die Seite wurde entfernt. Sag uns, wonach du suchst, und wir finden es.',
    suggestionsTitle: 'Vielleicht suchst du',
    bestMatchTitle: 'Meintest du',
    prefillHint: 'Aus der aufgerufenen Adresse übernommen',
    goHome: 'Startseite',
    browseAll: 'Alle Kategorien',
    reportBrokenLink: 'Defekten Link melden',
    noticeTitle: 'Diese Seite ist nicht verfügbar',
    noticeSearching: 'Wir suchen stattdessen nach {query} …',
    noticePrefilled: 'Suche unten nach {query} oder sag uns, was du brauchst.',
    errorTitle: 'Etwas ist schiefgelaufen',
    errorBody: 'Wir konnten diese Seite nicht laden. Versuch es erneut oder suche unten, was du brauchst.',
    retry: 'Erneut versuchen',
    streamLostTitle: 'Wir konnten diese Seite nicht fertigstellen',
    streamLostBody: 'Die Suche wurde beendet, bevor die Seite gespeichert war. Starten Sie sie erneut, wir knüpfen an Ihre Worte an.',
    relaunch: 'Erneut suchen',
  },
  [LanguageCode.italian]: {
    eyebrow: 'Pagina non trovata',
    title: 'Non abbiamo trovato quella pagina',
    body: 'Il link potrebbe essere obsoleto o la pagina è stata rimossa. Dicci cosa cerchi e lo troveremo.',
    suggestionsTitle: 'Forse cercavi',
    bestMatchTitle: 'Intendevi',
    prefillHint: 'Precompilato dall’indirizzo che hai richiesto',
    goHome: 'Home',
    browseAll: 'Tutte le categorie',
    reportBrokenLink: 'Segnala un link non funzionante',
    noticeTitle: 'Quella pagina non è disponibile',
    noticeSearching: 'Cerchiamo invece {query}…',
    noticePrefilled: 'Cerca {query} qui sotto, o dicci cosa ti serve.',
    errorTitle: 'Qualcosa è andato storto',
    errorBody: 'Non siamo riusciti a caricare questa pagina. Riprova, oppure cerca qui sotto ciò che ti serve.',
    retry: 'Riprova',
    streamLostTitle: 'Non siamo riusciti a completare questa pagina',
    streamLostBody: 'La ricerca si è interrotta prima del salvataggio della pagina. Avviala di nuovo e ripartiremo dalle tue parole.',
    relaunch: 'Cerca di nuovo',
  },
  [LanguageCode.portugueseBR]: {
    eyebrow: 'Página não encontrada',
    title: 'Não encontramos essa página',
    body: 'O link pode estar desatualizado ou a página foi removida. Diga o que você procura e nós encontramos.',
    suggestionsTitle: 'Talvez você procure',
    bestMatchTitle: 'Você quis dizer',
    prefillHint: 'Preenchido a partir do endereço que você tentou',
    goHome: 'Início',
    browseAll: 'Todas as categorias',
    reportBrokenLink: 'Relatar link quebrado',
    noticeTitle: 'Essa página não está disponível',
    noticeSearching: 'Estamos buscando {query} no lugar…',
    noticePrefilled: 'Busque {query} abaixo, ou diga o que você precisa.',
    errorTitle: 'Algo deu errado',
    errorBody: 'Não conseguimos carregar esta página. Tente de novo, ou busque abaixo o que você precisa.',
    retry: 'Tentar de novo',
    streamLostTitle: 'Não conseguimos concluir esta página',
    streamLostBody: 'A busca parou antes de a página ser salva. Inicie de novo e retomamos a partir das suas palavras.',
    relaunch: 'Buscar de novo',
  },
  [LanguageCode.portuguesePT]: {
    eyebrow: 'Página não encontrada',
    title: 'Não encontrámos essa página',
    body: 'O link pode estar desatualizado ou a página foi removida. Diga-nos o que procura e nós encontramos.',
    suggestionsTitle: 'Talvez procure',
    bestMatchTitle: 'Queria dizer',
    prefillHint: 'Preenchido a partir do endereço que tentou',
    goHome: 'Início',
    browseAll: 'Todas as categorias',
    reportBrokenLink: 'Comunicar ligação partida',
    noticeTitle: 'Essa página não está disponível',
    noticeSearching: 'Estamos a procurar {query} em vez disso…',
    noticePrefilled: 'Procure {query} abaixo, ou diga-nos o que precisa.',
    errorTitle: 'Algo correu mal',
    errorBody: 'Não conseguimos carregar esta página. Tente novamente, ou procure abaixo o que precisa.',
    retry: 'Tentar novamente',
    streamLostTitle: 'Não conseguimos concluir esta página',
    streamLostBody: 'A pesquisa parou antes de a página ser guardada. Inicie de novo e retomamos a partir das suas palavras.',
    relaunch: 'Pesquisar de novo',
  },
  [LanguageCode.dutch]: {
    eyebrow: 'Pagina niet gevonden',
    title: 'We konden die pagina niet vinden',
    body: 'De link is mogelijk verouderd of de pagina is verwijderd. Vertel ons wat je zoekt en wij vinden het.',
    suggestionsTitle: 'Misschien zoek je',
    bestMatchTitle: 'Bedoelde je',
    prefillHint: 'Ingevuld op basis van het adres dat je probeerde',
    goHome: 'Home',
    browseAll: 'Alle categorieën',
    reportBrokenLink: 'Verbroken link melden',
    noticeTitle: 'Die pagina is niet beschikbaar',
    noticeSearching: 'We zoeken in plaats daarvan naar {query}…',
    noticePrefilled: 'Zoek hieronder naar {query}, of vertel ons wat je nodig hebt.',
    errorTitle: 'Er is iets misgegaan',
    errorBody: 'We konden deze pagina niet laden. Probeer het opnieuw, of zoek hieronder wat je nodig hebt.',
    retry: 'Opnieuw proberen',
    streamLostTitle: 'We konden deze pagina niet afmaken',
    streamLostBody: 'De zoekopdracht stopte voordat de pagina was opgeslagen. Start opnieuw en we gaan verder met uw woorden.',
    relaunch: 'Opnieuw zoeken',
  },
  [LanguageCode.polish]: {
    eyebrow: 'Nie znaleziono strony',
    title: 'Nie udało się znaleźć tej strony',
    body: 'Link może być nieaktualny lub strona została usunięta. Powiedz nam, czego szukasz, a my to znajdziemy.',
    suggestionsTitle: 'Być może szukasz',
    bestMatchTitle: 'Czy chodziło o',
    prefillHint: 'Uzupełnione na podstawie adresu, który otworzyłeś',
    goHome: 'Strona główna',
    browseAll: 'Wszystkie kategorie',
    reportBrokenLink: 'Zgłoś niedziałający link',
    noticeTitle: 'Ta strona jest niedostępna',
    noticeSearching: 'Zamiast tego szukamy {query}…',
    noticePrefilled: 'Wyszukaj {query} poniżej albo powiedz nam, czego potrzebujesz.',
    errorTitle: 'Coś poszło nie tak',
    errorBody: 'Nie udało się wczytać tej strony. Spróbuj ponownie albo wyszukaj poniżej, czego potrzebujesz.',
    retry: 'Spróbuj ponownie',
    streamLostTitle: 'Nie udało się ukończyć tej strony',
    streamLostBody: 'Wyszukiwanie zatrzymało się, zanim strona została zapisana. Uruchom je ponownie, a zaczniemy od Twoich słów.',
    relaunch: 'Szukaj ponownie',
  },
  [LanguageCode.swedish]: {
    eyebrow: 'Sidan hittades inte',
    title: 'Vi hittade inte den sidan',
    body: 'Länken kan vara inaktuell eller så har sidan tagits bort. Berätta vad du söker så hittar vi det.',
    suggestionsTitle: 'Du kanske söker',
    bestMatchTitle: 'Menade du',
    prefillHint: 'Ifyllt från adressen du försökte öppna',
    goHome: 'Hem',
    browseAll: 'Alla kategorier',
    reportBrokenLink: 'Rapportera en trasig länk',
    noticeTitle: 'Den sidan är inte tillgänglig',
    noticeSearching: 'Vi söker efter {query} i stället…',
    noticePrefilled: 'Sök efter {query} nedan, eller berätta vad du behöver.',
    errorTitle: 'Något gick fel',
    errorBody: 'Vi kunde inte läsa in den här sidan. Försök igen, eller sök nedan efter det du behöver.',
    retry: 'Försök igen',
    streamLostTitle: 'Vi kunde inte göra klart den här sidan',
    streamLostBody: 'Sökningen stannade innan sidan sparades. Starta om den så fortsätter vi från dina ord.',
    relaunch: 'Sök igen',
  },
  [LanguageCode.japanese]: {
    eyebrow: 'ページが見つかりません',
    title: 'そのページは見つかりませんでした',
    body: 'リンクが古いか、ページが削除された可能性があります。お探しのものをお知らせいただければお探しします。',
    suggestionsTitle: 'お探しのものはこちらですか',
    bestMatchTitle: 'もしかして',
    prefillHint: 'アクセスされたアドレスから入力しました',
    goHome: 'ホーム',
    browseAll: 'すべてのカテゴリー',
    reportBrokenLink: 'リンク切れを報告',
    noticeTitle: 'このページはご利用いただけません',
    noticeSearching: '代わりに{query}を検索しています…',
    noticePrefilled: '下で{query}を検索するか、お探しのものをお知らせください。',
    errorTitle: '問題が発生しました',
    errorBody: 'このページを読み込めませんでした。もう一度お試しいただくか、下でお探しのものを検索してください。',
    retry: '再試行',
    streamLostTitle: 'このページを完成できませんでした',
    streamLostBody: 'ページが保存される前に検索が止まりました。もう一度検索すると、入力内容から再開します。',
    relaunch: 'もう一度検索',
  },
};

export const notFoundUiContent: Record<LanguageType, NotFoundUiContent> =
  proxyLanguageRecord<NotFoundUiContent>(notFoundUiContentCore);

export const getNotFoundUiContent = (locale: LocaleDetails): NotFoundUiContent =>
  notFoundUiContent[locale.languageCode] ?? notFoundUiContent.en;
